import { Component, HostListener, OnInit } from "@angular/core";
import { PageLayout } from "@cvx/nextpage";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialog } from "@angular/material/dialog";
import { GraphPermissionTierReviewService } from "src/app/services/graph/GraphPermissionTierReview.service";
import { PermissionTier, PermissionTierLabels, PermissionTierReviewStatus, IGraphPermissionTierReview } from "src/app/models/graph/applications/GraphPermissionTierReview";
import { DialogDirectorySearchComponent } from "../../_shared/dialog-directory-search/dialog-directory-search.component";
import { IDialogDirectoryData } from "src/app/models/components/IDialogDirectoryData";
import { DirectoryDomain } from "src/app/models/directory/enums/DirectoryDomain.enum";
import { GroupMemberType } from "src/app/models/directory/enums/GroupMemberType.enum";
import { UserType } from "src/app/models/directory/enums/UserType.enum";
import { DirectoryObjectBase } from "src/app/models/directory/DirectoryObjectBase";
import { DirectoryUserService } from "src/app/services/directory/DirectoryUser.service";

interface ReviewRow extends IGraphPermissionTierReview {
    approverDisplayNames: string[];
    reviewerDisplayName: string;
}

interface BulkApprover {
    value: string;
    displayName: string;
}

const PAGE_SIZE = 10;
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
    selector: 'app-graph-permission-tier-review',
    templateUrl: './graph-permission-tier-review.component.html',
    styleUrls: ['./graph-permission-tier-review.component.css']
})
export class GraphPermissionTierReviewComponent implements OnInit {
    PageLayout = PageLayout;
    Math = Math;
    tierOptions = Object.values(PermissionTier);
    tierLabels = PermissionTierLabels;
    pageSize = PAGE_SIZE;

    isLoading = false;
    loadErrorMessage = '';
    allRows: ReviewRow[] = [];
    searchTerm = '';

    activeTab: 'needsReview' | 'reviewed' = 'needsReview';
    page = 1;

    selectedIds = new Set<string>();
    isBulkSaving = false;
    bulkTier: PermissionTier = PermissionTier.Tier0;
    bulkApprovers: BulkApprover[] = [];
    isBulkTierMenuOpen = false;

    constructor(
        private graphPermissionTierReviewService: GraphPermissionTierReviewService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private directoryUserService: DirectoryUserService
    ) { }

    ngOnInit(): void {
        this.load();
    }

    @HostListener('document:click')
    closeBulkTierMenu(): void {
        this.isBulkTierMenuOpen = false;
    }

    toggleBulkTierMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.isBulkTierMenuOpen = !this.isBulkTierMenuOpen;
    }

    selectBulkTier(tier: PermissionTier): void {
        this.bulkTier = tier;
        this.isBulkTierMenuOpen = false;
    }

    async load(): Promise<void> {
        this.isLoading = true;
        this.loadErrorMessage = '';
        this.page = 1;
        this.selectedIds.clear();
        try {
            const response = await this.graphPermissionTierReviewService.GetAllReviews();
            // Never show a raw GUID while resolution is pending/fails — only default the display name from the
            // raw value when it's already something a human can read (approvers are stored as emails; reviewer
            // is always a raw object ID, set server-side from the submitting/seeding principal).
            this.allRows = response.data.map(r => ({
                ...r,
                approverDisplayNames: (r.approvers ?? []).map(a => GUID_PATTERN.test(a) ? '' : a),
                reviewerDisplayName: r.reviewer && GUID_PATTERN.test(r.reviewer) ? '' : r.reviewer
            }));
            await this.resolveDisplayNames();
        } catch (ex: any) {
            this.loadErrorMessage = ex?.error?.message ?? 'Failed to load Graph permission tier reviews.';
        } finally {
            this.isLoading = false;
        }
    }

    // Approver and reviewer are both sometimes stored as a raw object ID (e.g. reviewer is set to the
    // submitting user's ObjectId server-side) instead of a friendly name — resolve those back to a display
    // name instead of showing a bare GUID. Resolved one-by-one (not batched): the batch endpoint 404s its
    // *entire* response if even one id in the batch isn't a real, resolvable user, which would otherwise
    // leave every name unresolved.
    private async resolveDisplayNames(): Promise<void> {
        const idsToResolve = Array.from(new Set([
            ...this.allRows.flatMap(r => r.approvers ?? []).filter(a => GUID_PATTERN.test(a)),
            ...this.allRows.filter(r => r.reviewer && GUID_PATTERN.test(r.reviewer)).map(r => r.reviewer)
        ]));
        if (idsToResolve.length === 0) return;

        const results = await Promise.allSettled(
            idsToResolve.map(id => this.directoryUserService.GetByIdAsync({ id, domain: DirectoryDomain.Chevron }))
        );

        const nameById = new Map<string, string>();
        results.forEach((result, i) => {
            if (result.status === 'fulfilled' && result.value?.displayName) {
                nameById.set(idsToResolve[i], result.value.displayName);
            }
        });

        // A GUID that couldn't be resolved (e.g. points at a deleted/corrupted user) still must never render as
        // the raw ID — fall back to a clear placeholder instead.
        this.allRows.forEach(r => {
            r.approverDisplayNames = (r.approvers ?? []).map(a => GUID_PATTERN.test(a) ? (nameById.get(a) ?? 'Unknown user') : a);
            if (r.reviewer && GUID_PATTERN.test(r.reviewer)) r.reviewerDisplayName = nameById.get(r.reviewer) ?? 'Unknown user';
        });
    }

    get filteredRows(): ReviewRow[] {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) return this.allRows;
        return this.allRows.filter(r =>
            r.name.toLowerCase().includes(term) || (r.description ?? '').toLowerCase().includes(term));
    }

    get needsReviewRows(): ReviewRow[] {
        return this.filteredRows.filter(r => r.tierReviewStatus === PermissionTierReviewStatus.PendingReview);
    }

    get reviewedRows(): ReviewRow[] {
        return this.filteredRows.filter(r => r.tierReviewStatus === PermissionTierReviewStatus.Reviewed);
    }

    get activeRows(): ReviewRow[] {
        return this.activeTab === 'needsReview' ? this.needsReviewRows : this.reviewedRows;
    }

    switchTab(tab: 'needsReview' | 'reviewed'): void {
        this.activeTab = tab;
        this.page = 1;
    }

    onSearchChange(): void {
        this.page = 1;
    }

    pagedSlice(rows: ReviewRow[], page: number): ReviewRow[] {
        const start = (page - 1) * this.pageSize;
        return rows.slice(start, start + this.pageSize);
    }

    totalPages(rows: ReviewRow[]): number {
        return Math.max(1, Math.ceil(rows.length / this.pageSize));
    }

    goToPage(rows: ReviewRow[], page: number): void {
        this.page = Math.min(Math.max(1, page), this.totalPages(rows));
    }

    isSelected(row: ReviewRow): boolean {
        return this.selectedIds.has(row.permissionId);
    }

    toggleSelected(row: ReviewRow): void {
        if (this.selectedIds.has(row.permissionId)) this.selectedIds.delete(row.permissionId);
        else this.selectedIds.add(row.permissionId);
    }

    isAllSelected(rows: ReviewRow[]): boolean {
        return rows.length > 0 && rows.every(r => this.selectedIds.has(r.permissionId));
    }

    toggleSelectAll(rows: ReviewRow[]): void {
        if (this.isAllSelected(rows)) rows.forEach(r => this.selectedIds.delete(r.permissionId));
        else rows.forEach(r => this.selectedIds.add(r.permissionId));
    }

    clearSelection(): void {
        this.selectedIds.clear();
    }

    addBulkApprover(): void {
        const dialogRef = this.dialog.open(DialogDirectorySearchComponent, {
            disableClose: true,
            autoFocus: true,
            maxWidth: 1000,
            width: '100%',
            data: {
                type: GroupMemberType.User,
                domain: DirectoryDomain.Chevron,
                userType: UserType.Primary,
                filterGroupRemoveNotAvailableAsGroupMember: false,
                filterGroupRemoveDynamicMembershipEnabled: false,
                filterGroupOnlyManagedByIdamp: false
            } as IDialogDirectoryData
        });

        dialogRef.afterClosed().subscribe((result: DirectoryObjectBase) => {
            if (!result) return;
            // Owner/approver is stored as an email/UPN elsewhere in this system — a user with no mail can't be
            // reliably resolved later (their object id alone isn't guaranteed to even be a valid Graph reference).
            if (!result.mail) {
                this.snackBar.open(`${result.displayName || 'That user'} has no email on file and can't be set as an approver.`, 'Dismiss', { duration: 6000 });
                return;
            }
            if (this.bulkApprovers.some(a => a.value === result.mail)) return;
            this.bulkApprovers = [...this.bulkApprovers, { value: result.mail, displayName: result.displayName }];
        });
    }

    removeBulkApprover(value: string): void {
        this.bulkApprovers = this.bulkApprovers.filter(a => a.value !== value);
    }

    async applyBulk(): Promise<void> {
        if (this.selectedIds.size === 0 || this.bulkApprovers.length === 0) return;
        this.isBulkSaving = true;
        const ids = Array.from(this.selectedIds);
        const approverValues = this.bulkApprovers.map(a => a.value);
        const approverDisplayNames = this.bulkApprovers.map(a => a.displayName);
        try {
            await this.graphPermissionTierReviewService.SubmitReview({
                permissionIds: ids,
                tier: this.bulkTier,
                approvers: approverValues
            });
            this.allRows
                .filter(r => this.selectedIds.has(r.permissionId))
                .forEach(r => {
                    r.tier = this.bulkTier;
                    r.tierReviewStatus = PermissionTierReviewStatus.Reviewed;
                    r.approvers = approverValues;
                    r.approverDisplayNames = approverDisplayNames;
                });
            this.snackBar.open(`${ids.length} permission(s) saved`, 'Dismiss', { duration: 4000 });
            this.selectedIds.clear();
            this.bulkApprovers = [];
        } catch (ex: any) {
            this.snackBar.open(ex?.error?.message ?? 'Failed to save selected permissions', 'Dismiss', { duration: 6000 });
        } finally {
            this.isBulkSaving = false;
        }
    }

    tierBadgeStyle(tier: PermissionTier): { [key: string]: string } {
        switch (tier) {
            case PermissionTier.Tier0:      return { background: '#fdf2f2', color: '#b42318', border: '1px solid #f6d6d5' };
            case PermissionTier.Tier1:      return { background: '#fdf6e8', color: '#946200', border: '1px solid #f3e6bf' };
            case PermissionTier.Tier2:      return { background: '#eef4ff', color: '#1d4ed8', border: '1px solid #d6e4ff' };
            case PermissionTier.Tier3:      return { background: '#effaf3', color: '#15803d', border: '1px solid #cfeedb' };
            default:                        return { background: '#f4f5f6', color: '#4b5563', border: '1px solid #e5e7eb' };
        }
    }
}
